import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { CartQuote, OrderConfirmation } from '@/types'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { formatPickupDate, formatPrice, pluralize } from '@/lib/format'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button, buttonVariants } from '@/components/ui/button'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import Container from '@/components/generals/container'
import FormField, { describedBy } from '@/components/generals/form-field'
import ProductImage from '@/components/shop/product-image'
import QuantityStepper from '@/components/shop/quantity-stepper'
import { useCart } from '@/components/shop/cart-provider'
import { directionsUrl, storeInfo } from '@/constants/site'

import {
    isIsoDate,
    isValidEmail,
    isValidPhone,
    localIsoDate
} from '@/lib/validation'

import {
    ArrowRight,
    Check,
    CircleAlert,
    Clock,
    LoaderCircle,
    MapPin,
    Navigation,
    ShoppingBag,
    Trash2
} from 'lucide-react'

type FormState = {
    name: string
    email: string
    phone: string
    pickupDate: string
    notes: string
    company: string
}

type FieldName = keyof FormState

const EMPTY_FORM: FormState = {
    name: '',
    email: '',
    phone: '',
    pickupDate: '',
    notes: '',
    company: ''
}

const FIELD_ORDER: Array<FieldName> = ['name', 'email', 'phone', 'pickupDate']

const validate = (form: FormState, minDate: string): Partial<Record<FieldName, string>> => {
    const errors: Partial<Record<FieldName, string>> = {}

    if (form.name.trim().length < 2) errors.name = "Enter your name."
    if (!isValidEmail(form.email.trim())) errors.email = "Enter a valid email address."
    if (!isValidPhone(form.phone.trim())) errors.phone = "Enter a phone number we can reach you on."
    if (!isIsoDate(form.pickupDate)) errors.pickupDate = "Choose the day you will pick up."
    else if (minDate && form.pickupDate < minDate) errors.pickupDate = "Choose today or a later day."

    return errors
}

const CartPage: FC = (): ReactNode => {
    const {
        lines,
        ready,
        itemCount: cartCount,
        subtotal: cartSubtotal,
        setQuantity,
        removeItem,
        clear,
        syncLines
    } = useCart()

    const [quote, setQuote] = useState<CartQuote | null>(null)
    const [checking, setChecking] = useState(false)
    const [quoteFailed, setQuoteFailed] = useState(false)
    const [form, setForm] = useState<FormState>(EMPTY_FORM)
    const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({})
    const [formError, setFormError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [confirmation, setConfirmation] = useState<OrderConfirmation | null>(null)
    const [dateRange, setDateRange] = useState({ min: '', max: '' })
    const formRef = useRef<HTMLFormElement>(null)

    const signature = lines.map(line => `${line.key}:${line.quantity}`).join('|')

    useEffect(() => {
        setDateRange({ min: localIsoDate(0), max: localIsoDate(90) })
    }, [])

    useEffect(() => {
        if (!ready) return

        if (lines.length === 0) {
            setQuote(null)
            setQuoteFailed(false)

            return
        }

        const controller = new AbortController()

        setChecking(true)

        fetch('/api/cart/quote', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                lines: lines.map(line => ({ productId: line.product_id, variant: line.variant, quantity: line.quantity }))
            }),
            signal: controller.signal
        })
            .then(async response => {
                if (!response.ok) throw new Error(`status ${response.status}`)

                return await response.json() as CartQuote
            })
            .then(data => {
                setQuote(data)
                setQuoteFailed(false)
                syncLines(data.lines)
            })
            .catch(() => {
                if (!controller.signal.aborted) setQuoteFailed(true)
            })
            .finally(() => {
                if (!controller.signal.aborted) setChecking(false)
            })

        return () => controller.abort()
    // Re-check only when items or quantities change, not when names or prices refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, signature])

    const quoted = new Map((quote?.lines ?? []).map(line => [line.key, line]))
    const issues = (quote?.lines ?? []).filter(line => !line.available)
    const subtotal = quote ? quote.subtotal : cartSubtotal
    const itemCount = quote ? quote.itemCount : cartCount

    const update = (field: FieldName) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value

        setForm(current => ({ ...current, [field]: value }))

        if (errors[field]) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setFormError(null)

        const found = validate(form, dateRange.min)

        if (Object.keys(found).length > 0) {
            setErrors(found)

            const first = FIELD_ORDER.find(field => found[field])

            if (first) formRef.current?.querySelector<HTMLElement>(`#order-${first}`)?.focus()

            return
        }

        if (issues.length > 0) {
            setFormError("Remove or change the items marked above before reserving.")

            return
        }

        setSubmitting(true)

        try {
            const response = await fetch('/api/orders', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...form,
                    lines: lines.map(line => ({ productId: line.product_id, variant: line.variant, quantity: line.quantity }))
                })
            })

            const data = await response.json().catch(() => null)

            if (response.status === 409 && data?.quote) {
                setQuote(data.quote as CartQuote)
                setFormError(typeof data.message === 'string' ? data.message : "Some items need attention before you can reserve.")

                return
            }

            if (!response.ok) {
                if (data?.errors) setErrors(data.errors)

                setFormError(typeof data?.message === 'string' ? data.message : "Something went wrong. Please try again or call the shop.")

                return
            }

            setConfirmation(data as OrderConfirmation)
            setForm(EMPTY_FORM)
            clear()
            window.scrollTo({ top: 0 })
        }
        catch {
            setFormError("We could not reach the shop. Check your connection and try again.")
        }
        finally {
            setSubmitting(false)
        }
    }

    if (confirmation) {
        return (
            <Layout>
                <Seo title='Reservation received' />

                <Container className='py-14 sm:py-20'>
                    <div className='mx-auto max-w-2xl'>
                        <div className='text-center'>
                            <span className='mx-auto flex size-16 items-center justify-center rounded-full bg-lagoon text-lagoon-foreground animate-in zoom-in-50 duration-500'>
                                <Check className='size-8' strokeWidth={2.5} />
                            </span>

                            <h1 className='mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl'>
                                Your gear is reserved
                            </h1>

                            <p className='mt-4 text-lg leading-relaxed text-muted-foreground'>
                                We will set everything aside for{' '}

                                <strong className='text-foreground'>
                                    {formatPickupDate(confirmation.pickupDate)}
                                </strong>

                                . You pay when you pick it up.
                            </p>
                        </div>

                        <div className='mt-10 rounded-[2rem] bg-card p-6 ring-1 ring-border sm:p-8'>
                            <div className='flex flex-wrap items-center justify-between gap-3 border-b border-border pb-5'>
                                <div>
                                    <p className='text-sm text-muted-foreground'>
                                        Reservation number
                                    </p>

                                    <p className='font-mono text-xl font-bold tracking-wider'>
                                        {confirmation.reference}
                                    </p>
                                </div>

                                <Badge variant='accent'>
                                    Pay at pickup
                                </Badge>
                            </div>

                            <ul className='divide-y divide-border'>
                                {confirmation.lines.map(line => (
                                    <li key={line.key} className='flex items-start justify-between gap-4 py-4'>
                                        <div className='min-w-0'>
                                            <p className='font-semibold'>
                                                <span data-mdlfy-cms={`products:${line.product_id}:name`}>
                                                    {line.name}
                                                </span>
                                            </p>

                                            <p className='mt-0.5 text-sm text-muted-foreground'>
                                                {line.variant && (
                                                    <span>
                                                        {line.variant},{' '}
                                                    </span>
                                                )}

                                                <span>
                                                    Qty {line.quantity}
                                                </span>
                                            </p>
                                        </div>

                                        <p className='shrink-0 font-semibold tabular-nums'>
                                            {formatPrice(line.price * line.quantity)}
                                        </p>
                                    </li>
                                ))}
                            </ul>

                            <div className='flex items-center justify-between border-t border-border pt-5'>
                                <span className='font-semibold'>
                                    To pay in store
                                </span>

                                <span className='font-heading text-2xl font-extrabold tabular-nums'>
                                    {formatPrice(confirmation.subtotal)}
                                </span>
                            </div>
                        </div>

                        <div className='mt-6 grid gap-4 rounded-3xl bg-secondary p-6 text-sm sm:grid-cols-2'>
                            <p className='flex gap-3'>
                                <MapPin className='mt-0.5 size-5 shrink-0 text-tide' />

                                <span>
                                    <span className='block font-semibold'>
                                        Pick up at
                                    </span>

                                    {storeInfo.street}, {storeInfo.city}
                                </span>
                            </p>

                            <div className='flex gap-3'>
                                <Clock className='mt-0.5 size-5 shrink-0 text-tide' />

                                <dl>
                                    {storeInfo.hours.map(entry => (
                                        <div key={entry.days} className='flex flex-wrap gap-x-1.5'>
                                            <dt className='font-semibold'>
                                                {entry.days}
                                            </dt>

                                            <dd>
                                                {entry.time}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        </div>

                        <p className='mt-6 text-center text-sm text-muted-foreground'>
                            Keep your reservation number handy. If plans change, call us on{' '}

                            <a href={storeInfo.phoneHref} className='font-semibold text-foreground underline underline-offset-4'>
                                {storeInfo.phone}
                            </a>

                            .
                        </p>

                        <div className='mt-8 flex flex-wrap justify-center gap-3'>
                            <Link href='/shop' className={buttonVariants({ variant: 'accent', size: 'lg' })}>
                                Back to the shop
                                <ArrowRight />
                            </Link>

                            <a
                                href={directionsUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className={buttonVariants({ variant: 'outline', size: 'lg' })}
                            >
                                <Navigation />
                                Get directions
                            </a>
                        </div>
                    </div>
                </Container>
            </Layout>
        )
    }

    return (
        <Layout>
            <Seo
                title='Your cart'
                description="Review your gear and reserve it for pickup. No payment online, you pay at the counter when you collect."
            />

            <Container className='py-10 sm:py-14'>
                <h1 className='text-4xl font-extrabold tracking-tight sm:text-5xl'>
                    Your cart
                </h1>

                <p className='mt-3 max-w-2xl text-lg text-muted-foreground'>
                    Reserve now and we will hold everything at the counter. Nothing is charged online.
                </p>

                {!ready && (
                    <div className='mt-10 space-y-4' aria-busy='true' aria-label='Loading your cart'>
                        {[0, 1].map(item => (
                            <div key={item} className='h-28 animate-pulse rounded-3xl bg-muted' />
                        ))}
                    </div>
                )}

                {ready && lines.length === 0 && (
                    <div className='mt-10 flex flex-col items-center rounded-[2rem] bg-card px-6 py-16 text-center ring-1 ring-border'>
                        <span className='flex size-16 items-center justify-center rounded-full bg-secondary text-primary'>
                            <ShoppingBag className='size-7' strokeWidth={1.5} />
                        </span>

                        <p className='mt-5 font-heading text-2xl font-bold'>
                            Your cart is empty
                        </p>

                        <p className='mt-2 max-w-md text-muted-foreground'>
                            Browse the wall, add what you need, and we will have it waiting when you come in.
                        </p>

                        <Link href='/shop' className={cn(buttonVariants({ variant: 'accent', size: 'lg' }), 'mt-8')}>
                            Browse the shop
                            <ArrowRight />
                        </Link>
                    </div>
                )}

                {ready && lines.length > 0 && (
                    <div className='mt-10 grid gap-10 lg:grid-cols-12 lg:items-start'>
                        <section aria-labelledby='cart-items-title' className='lg:col-span-7'>
                            <div className='flex items-center justify-between border-b border-border pb-4'>
                                <h2 id='cart-items-title' className='text-xl font-bold'>
                                    {lines.length} {pluralize(lines.length, 'item')} in your cart
                                </h2>

                                {checking && (
                                    <span className='flex items-center gap-2 text-sm text-muted-foreground'>
                                        <LoaderCircle className='size-4 animate-spin' />
                                        Checking prices
                                    </span>
                                )}
                            </div>

                            <ul className='divide-y divide-border'>
                                {lines.map(line => {
                                    const fresh = quoted.get(line.key)
                                    const missing = fresh?.missing ?? false
                                    const name = fresh && !missing ? fresh.name : line.name
                                    const price = fresh && !missing ? fresh.price : line.price
                                    const slug = fresh && !missing ? fresh.slug : line.slug

                                    return (
                                        <li key={line.key} className='flex gap-4 py-5 sm:gap-6'>
                                            <Link
                                                href={`/shop/${slug}`}
                                                tabIndex={-1}
                                                aria-hidden='true'
                                                className='relative size-24 shrink-0 overflow-hidden rounded-2xl ring-1 ring-border sm:size-28'
                                            >
                                                <ProductImage
                                                    src={fresh && !missing ? fresh.image : line.image}
                                                    alt={line.image_alt ?? name}
                                                    categorySlug={line.category_slug}
                                                    iconClassName='size-8'
                                                />
                                            </Link>

                                            <div className='flex min-w-0 flex-1 flex-col'>
                                                <div className='flex flex-wrap items-start justify-between gap-x-4 gap-y-1'>
                                                    <div className='min-w-0'>
                                                        {missing
                                                            ? (
                                                                <p className='font-semibold'>
                                                                    {name}
                                                                </p>
                                                            )
                                                            : (
                                                                <Link
                                                                    href={`/shop/${slug}`}
                                                                    className='font-semibold underline-offset-4 hover:underline'
                                                                >
                                                                    <span data-mdlfy-cms={`products:${line.product_id}:name`}>
                                                                        {name}
                                                                    </span>
                                                                </Link>
                                                            )}

                                                        {line.variant && (
                                                            <p className='mt-0.5 text-sm text-muted-foreground'>
                                                                {line.variant}
                                                            </p>
                                                        )}

                                                        <p className='mt-0.5 text-sm text-muted-foreground'>
                                                            <span>
                                                                {formatPrice(price)}
                                                            </span>
                                                            {' '}each
                                                        </p>
                                                    </div>

                                                    <p className='font-heading text-lg font-bold tabular-nums'>
                                                        {formatPrice(price * line.quantity)}
                                                    </p>
                                                </div>

                                                {fresh?.issue && (
                                                    <p className='mt-3 flex items-start gap-2 rounded-xl bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive'>
                                                        <CircleAlert className='mt-0.5 size-4 shrink-0' />
                                                        {fresh.issue}
                                                    </p>
                                                )}

                                                <div className='mt-auto flex items-center justify-between gap-3 pt-4'>
                                                    <QuantityStepper
                                                        size='sm'
                                                        value={line.quantity}
                                                        label={name}
                                                        onChange={quantity => setQuantity(line.key, quantity)}
                                                    />

                                                    <Button
                                                        variant='ghost'
                                                        size='sm'
                                                        onClick={() => removeItem(line.key)}
                                                        className='text-muted-foreground'
                                                    >
                                                        <Trash2 />
                                                        Remove
                                                    </Button>
                                                </div>
                                            </div>
                                        </li>
                                    )
                                })}
                            </ul>

                            <Link
                                href='/shop'
                                className='mt-4 inline-flex items-center gap-2 text-sm font-semibold underline-offset-4 hover:underline'
                            >
                                Keep shopping
                                <ArrowRight className='size-4' />
                            </Link>
                        </section>

                        <section aria-labelledby='pickup-form-title' className='lg:sticky lg:top-32 lg:col-span-5'>
                            <div className='rounded-[2rem] bg-card p-6 ring-1 ring-border sm:p-8'>
                                <h2 id='pickup-form-title' className='text-2xl font-extrabold tracking-tight'>
                                    Reserve for pickup
                                </h2>

                                <p className='mt-1 text-sm text-muted-foreground'>
                                    No payment now. You pay by card or cash when you collect.
                                </p>

                                <dl className='mt-6 space-y-2 rounded-2xl bg-muted/60 p-4 text-sm'>
                                    <div className='flex justify-between'>
                                        <dt className='text-muted-foreground'>
                                            Items
                                        </dt>

                                        <dd className='font-semibold tabular-nums'>
                                            {itemCount}
                                        </dd>
                                    </div>

                                    <div className='flex items-baseline justify-between border-t border-border pt-2'>
                                        <dt className='font-semibold'>
                                            To pay at pickup
                                        </dt>

                                        <dd className='font-heading text-2xl font-extrabold tabular-nums'>
                                            {formatPrice(subtotal)}
                                        </dd>
                                    </div>
                                </dl>

                                {quoteFailed && (
                                    <p className='mt-3 text-xs text-muted-foreground'>
                                        We could not check the latest prices just now. We will confirm the total when you reserve.
                                    </p>
                                )}

                                <form ref={formRef} noValidate onSubmit={submit} className='mt-6 space-y-5'>
                                    <FormField id='order-name' label='Your name' error={errors.name}>
                                        <Input
                                            id='order-name'
                                            name='name'
                                            autoComplete='name'
                                            value={form.name}
                                            onChange={update('name')}
                                            aria-invalid={Boolean(errors.name)}
                                            aria-describedby={describedBy('order-name', errors.name)}
                                        />
                                    </FormField>

                                    <div className='grid gap-5 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2'>
                                        <FormField id='order-email' label='Email' error={errors.email}>
                                            <Input
                                                id='order-email'
                                                name='email'
                                                type='email'
                                                autoComplete='email'
                                                inputMode='email'
                                                value={form.email}
                                                onChange={update('email')}
                                                aria-invalid={Boolean(errors.email)}
                                                aria-describedby={describedBy('order-email', errors.email)}
                                            />
                                        </FormField>

                                        <FormField id='order-phone' label='Phone' error={errors.phone}>
                                            <Input
                                                id='order-phone'
                                                name='phone'
                                                type='tel'
                                                autoComplete='tel'
                                                inputMode='tel'
                                                value={form.phone}
                                                onChange={update('phone')}
                                                aria-invalid={Boolean(errors.phone)}
                                                aria-describedby={describedBy('order-phone', errors.phone)}
                                            />
                                        </FormField>
                                    </div>

                                    <FormField
                                        id='order-pickupDate'
                                        label='Pickup day'
                                        error={errors.pickupDate}
                                        hint="We're open every day of the week."
                                    >
                                        <Input
                                            id='order-pickupDate'
                                            name='pickupDate'
                                            type='date'
                                            min={dateRange.min || undefined}
                                            max={dateRange.max || undefined}
                                            value={form.pickupDate}
                                            onChange={update('pickupDate')}
                                            aria-invalid={Boolean(errors.pickupDate)}
                                            aria-describedby={describedBy('order-pickupDate', errors.pickupDate, "We're open every day of the week.")}
                                        />
                                    </FormField>

                                    <FormField id='order-notes' label='Notes' optional>
                                        <Textarea
                                            id='order-notes'
                                            name='notes'
                                            rows={3}
                                            maxLength={1000}
                                            value={form.notes}
                                            onChange={update('notes')}
                                            placeholder='A time you plan to come in, or line you want spooled'
                                        />
                                    </FormField>

                                    <div aria-hidden='true' className='absolute -left-[9999px] h-0 w-0 overflow-hidden'>
                                        <label htmlFor='order-company'>
                                            Company
                                        </label>

                                        <input
                                            id='order-company'
                                            name='company'
                                            tabIndex={-1}
                                            autoComplete='off'
                                            value={form.company}
                                            onChange={update('company')}
                                        />
                                    </div>

                                    {formError && (
                                        <p role='alert' className='flex items-start gap-2 rounded-xl bg-destructive/10 px-3 py-2.5 text-sm font-medium text-destructive'>
                                            <CircleAlert className='mt-0.5 size-4 shrink-0' />
                                            {formError}
                                        </p>
                                    )}

                                    <Button
                                        type='submit'
                                        size='lg'
                                        variant='accent'
                                        disabled={submitting || issues.length > 0}
                                        className='w-full'
                                    >
                                        {submitting
                                            ? (
                                                <>
                                                    <LoaderCircle className='size-5 animate-spin' />
                                                    Sending your reservation
                                                </>
                                            )
                                            : (
                                                <>
                                                    Reserve for pickup
                                                    <ArrowRight />
                                                </>
                                            )}
                                    </Button>

                                    {issues.length > 0 && (
                                        <p className='text-center text-sm text-destructive'>
                                            Fix the {issues.length === 1 ? 'item' : 'items'} marked in your cart to continue.
                                        </p>
                                    )}

                                    <p className='text-center text-xs text-muted-foreground'>
                                        We only use your details to contact you about this order.
                                    </p>
                                </form>
                            </div>
                        </section>
                    </div>
                )}
            </Container>
        </Layout>
    )
}

export default CartPage
